import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { THEATRE_EXPERIENCE_ICONS } from "../constants/theatreExperienceIcons";
import * as Constants from "../api/constants";
import { normalizeTheaterFolderKey } from "../utils/theaterGalleryImages";
import AdminDateInput from "./AdminDateInput";

function rupees(n) {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return `₹${v}`;
}

function displaySlotOfferValue(o) {
  if (!o || o.offerBasePrice == null) return "—";
  const k = String(o.offerPriceKind || "RS").toUpperCase();
  if (k === "PCT") return `${o.offerBasePrice}%`;
  return rupees(o.offerBasePrice);
}

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** One editable row for slots_meta via /addUpdateTimeslot */
function SlotRowEditor({ slot, theatreName, onSaved, showBanner }) {
  const [draft, setDraft] = useState(() => ({ ...slot }));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft({ ...slot });
  }, [slot]);

  const patch = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const promoKindNormalized = () => {
    const k = String(draft.slotPromoKind || "").toUpperCase();
    if (!k || k === "NONE") return { kind: null, value: null };
    const v =
      draft.slotPromoValue === "" ? null : Number(draft.slotPromoValue);
    if (v == null || Number.isNaN(v) || v <= 0) return { kind: null, value: null };
    return { kind: k, value: v };
  };

  const save = () => {
    setBusy(true);
    const { kind: pk, value: pv } = promoKindNormalized();
    const payload = {
      id: draft.id,
      theatreId: draft.theatreId,
      name: draft.name ?? "",
      slotTime: draft.slotTime ?? "",
      slotStartTime: draft.slotStartTime ?? "",
      slotEndTime: draft.slotEndTime ?? "",
      minGuests: draft.minGuests === "" ? null : Number(draft.minGuests),
      maxGuests: draft.maxGuests === "" ? null : Number(draft.maxGuests),
      basePrice: draft.basePrice === "" ? null : Number(draft.basePrice),
      extraPerPerson: draft.extraPerPerson === "" ? null : Number(draft.extraPerPerson),
      defaultCapacity:
        draft.defaultCapacity === "" ? null : Number(draft.defaultCapacity),
      isActive: draft.isActive === "Y" || draft.isActive === "y" ? "Y" : "N",
      isCustomSlot: draft.isCustomSlot || "N",
      slotInfo: draft.slotInfo || "",
      slotPromoKind: pk,
      slotPromoValue: pv,
    };
    axios
      .post(Constants.ADMIN_ADD_UPDATE_TIMESLOT, payload)
      .then(() => {
        showBanner("success", "Time slot saved.");
        onSaved?.();
      })
      .catch(() => showBanner("danger", "Could not save time slot."))
      .finally(() => setBusy(false));
  };

  return (
    <tr>
      <td className="small">{theatreName}</td>
      <td style={{ minWidth: 120 }}>
        <Form.Control size="sm" value={draft.name ?? ""} onChange={(e) => patch("name", e.target.value)} />
      </td>
      <td style={{ minWidth: 140 }}>
        <Form.Control
          size="sm"
          value={draft.slotTime ?? ""}
          onChange={(e) => patch("slotTime", e.target.value)}
        />
      </td>
      <td>
        <div className="d-flex flex-column gap-1">
          <Form.Control
            size="sm"
            placeholder="Start"
            value={draft.slotStartTime ?? ""}
            onChange={(e) => patch("slotStartTime", e.target.value)}
          />
          <Form.Control
            size="sm"
            placeholder="End"
            value={draft.slotEndTime ?? ""}
            onChange={(e) => patch("slotEndTime", e.target.value)}
          />
        </div>
      </td>
      <td>
        <div className="d-flex gap-1">
          <Form.Control
            size="sm"
            type="number"
            placeholder="Min"
            value={draft.minGuests ?? ""}
            onChange={(e) => patch("minGuests", e.target.value)}
          />
          <Form.Control
            size="sm"
            type="number"
            placeholder="Max"
            value={draft.maxGuests ?? ""}
            onChange={(e) => patch("maxGuests", e.target.value)}
          />
        </div>
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          value={draft.basePrice ?? ""}
          onChange={(e) => patch("basePrice", e.target.value)}
        />
      </td>
      <td>
        <Form.Select
          size="sm"
          value={
            draft.slotPromoKind && draft.slotPromoKind !== ""
              ? String(draft.slotPromoKind).toUpperCase()
              : "NONE"
          }
          onChange={(e) =>
            patch("slotPromoKind", e.target.value === "NONE" ? "" : e.target.value)
          }
        >
          <option value="NONE">No slot promo</option>
          <option value="FLAT">Flat ₹ off</option>
          <option value="PERCENT">% off listed base</option>
        </Form.Select>
      </td>
      <td style={{ minWidth: 88 }}>
        <Form.Control
          size="sm"
          type="number"
          min={0}
          disabled={
            !draft.slotPromoKind ||
            String(draft.slotPromoKind).toUpperCase() === "NONE"
          }
          placeholder={String(draft.slotPromoKind).toUpperCase() === "PERCENT" ? "%" : "₹"}
          value={draft.slotPromoValue ?? ""}
          onChange={(e) => patch("slotPromoValue", e.target.value)}
        />
      </td>
      <td>
        <Form.Select size="sm" value={draft.isActive === "Y" ? "Y" : "N"} onChange={(e) => patch("isActive", e.target.value)}>
          <option value="Y">Yes</option>
          <option value="N">No</option>
        </Form.Select>
      </td>
      <td>
        <div className="d-flex flex-column gap-1">
          <Form.Control
            size="sm"
            type="number"
            placeholder="extra/person"
            value={draft.extraPerPerson ?? ""}
            onChange={(e) => patch("extraPerPerson", e.target.value)}
          />
          <Form.Control
            size="sm"
            type="number"
            placeholder="capacity"
            value={draft.defaultCapacity ?? ""}
            onChange={(e) => patch("defaultCapacity", e.target.value)}
          />
          <Button type="button" size="sm" variant="outline-light" disabled={busy} onClick={save}>
            {busy ? "…" : "Save"}
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Theatre + timeslot overview and editing (metadata + optional date-range slot offers).
 */
export default function AdminTheatreDetailsTable({ showBanner }) {
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [filterTheatreId, setFilterTheatreId] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteKey, setNoteKey] = useState("");
  const [theatreSaving, setTheatreSaving] = useState(false);
  const [tDraft, setTDraft] = useState(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);

  const [hlLines, setHlLines] = useState([]);
  const [hlLoading, setHlLoading] = useState(false);
  const [hlSaving, setHlSaving] = useState(false);

  const [newOffer, setNewOffer] = useState(() => ({
    theatreId: "",
    slotId: "",
    validFrom: todayYmd(),
    validTo: todayYmd(),
    offerBasePrice: "",
    offerPriceKind: "RS",
    headline: "",
    isActive: "Y",
  }));

  const [iconPickerLineIndex, setIconPickerLineIndex] = useState(null);

  const loadMeta = useCallback(() => {
    setLoadingMeta(true);
    axios
      .get(Constants.GET_META_DATA_ADDONS)
      .then((res) => setMeta(res.data))
      .catch(() => {
        setMeta(null);
        showBanner("danger", "Could not load catalogue metadata (fetchMeta).");
      })
      .finally(() => setLoadingMeta(false));
  }, [showBanner]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const theatres = useMemo(() => {
    const raw = meta?.theatres;
    return Array.isArray(raw) ? raw : [];
  }, [meta]);

  const slotOffersAll = useMemo(() => {
    const raw = meta?.slotOffers;
    return Array.isArray(raw) ? raw : [];
  }, [meta]);

  const allSlotsFlat = useMemo(() => {
    const top = Array.isArray(meta?.timeslots) ? meta.timeslots : [];
    if (top.length) return top;
    return (theatres || []).flatMap((t) =>
      (Array.isArray(t.timeslots) ? t.timeslots : []).map((s) => ({
        ...s,
        theatreId: s.theatreId != null ? s.theatreId : t.id,
      }))
    );
  }, [meta, theatres]);

  const filteredTheatres = useMemo(() => {
    if (!filterTheatreId) return theatres;
    return theatres.filter((t) => String(t.id) === String(filterTheatreId));
  }, [theatres, filterTheatreId]);

  const slotsForFilter = useMemo(() => {
    if (!filterTheatreId) return allSlotsFlat;
    return allSlotsFlat.filter((s) => String(s.theatreId) === String(filterTheatreId));
  }, [allSlotsFlat, filterTheatreId]);

  const filteredSlotOffers = useMemo(() => {
    if (!filterTheatreId) return slotOffersAll;
    return slotOffersAll.filter((o) => String(o.theatreId) === String(filterTheatreId));
  }, [slotOffersAll, filterTheatreId]);

  const selectedTheatre = useMemo(() => {
    if (!filterTheatreId) return null;
    return theatres.find((t) => String(t.id) === String(filterTheatreId)) || null;
  }, [theatres, filterTheatreId]);

  useEffect(() => {
    if (!selectedTheatre) {
      setTDraft(null);
      return;
    }
    setTDraft({
      id: selectedTheatre.id,
      name: selectedTheatre.name ?? "",
      description: selectedTheatre.description ?? "",
      minGuests: selectedTheatre.minGuests ?? "",
      maxGuests: selectedTheatre.maxGuests ?? "",
      basePrice: selectedTheatre.basePrice ?? "",
      extraGuestPrice: selectedTheatre.extraGuestPrice ?? "",
      defaultCapacity: selectedTheatre.defaultCapacity ?? "",
      imageLocation: selectedTheatre.imageLocation ?? "",
      isActive: selectedTheatre.isActive ?? "Y",
      availableSlots: selectedTheatre.availableSlots ?? 0,
    });
  }, [selectedTheatre]);

  useEffect(() => {
    setNewOffer((prev) => ({
      ...prev,
      theatreId: filterTheatreId || "",
      slotId: "",
      offerPriceKind: "RS",
      offerBasePrice: "",
      headline: "",
    }));
    setEditingOfferId(null);
  }, [filterTheatreId]);

  const storageKeyForNote = selectedTheatre
    ? normalizeTheaterFolderKey(selectedTheatre.name)
    : "";

  useEffect(() => {
    if (!storageKeyForNote) {
      setNoteDraft("");
      setNoteKey("");
      return;
    }
    setNoteKey(storageKeyForNote);
    setNoteLoading(true);
    axios
      .get(Constants.ADMIN_THEATRE_GALLERY_NOTE, { params: { theaterKey: storageKeyForNote } })
      .then((res) => setNoteDraft(res.data?.text ?? ""))
      .catch(() => setNoteDraft(""))
      .finally(() => setNoteLoading(false));
  }, [storageKeyForNote]);

  useEffect(() => {
    if (!storageKeyForNote) {
      setHlLines([]);
      return;
    }
    setHlLoading(true);
    axios
      .get(Constants.ADMIN_THEATRE_GALLERY_EXPERIENCE_HIGHLIGHTS, {
        params: { theaterKey: storageKeyForNote },
      })
      .then((res) => {
        const raw = res.data?.lines;
        setHlLines(
          Array.isArray(raw) && raw.length
            ? raw.map((l) => ({
                icon: l.icon != null ? String(l.icon) : "",
                text: l.text != null ? String(l.text) : "",
              }))
            : []
        );
      })
      .catch(() => setHlLines([]))
      .finally(() => setHlLoading(false));
  }, [storageKeyForNote]);

  const saveHighlights = () => {
    if (!noteKey) return;
    setHlSaving(true);
    const lines = hlLines
      .map((l) => ({
        icon: (l.icon || "").trim(),
        text: (l.text || "").trim(),
      }))
      .filter((l) => l.text);
    axios
      .put(
        Constants.ADMIN_THEATRE_GALLERY_EXPERIENCE_HIGHLIGHTS,
        { theaterKey: noteKey, lines },
        { headers: { "Content-Type": "application/json" } }
      )
      .then(() =>
        showBanner("success", "Experience highlights saved next to theatre gallery files (JSON).")
      )
      .catch(() => showBanner("danger", "Could not save experience highlights."))
      .finally(() => setHlSaving(false));
  };

  const saveNote = () => {
    if (!noteKey) return;
    setNoteSaving(true);
    axios
      .put(
        Constants.ADMIN_THEATRE_GALLERY_NOTE,
        { theaterKey: noteKey, text: noteDraft },
        { headers: { "Content-Type": "application/json" } }
      )
      .then(() => showBanner("success", "Custom message saved next to theatre gallery files."))
      .catch(() => showBanner("danger", "Could not save theatre message."))
      .finally(() => setNoteSaving(false));
  };

  const saveTheatre = () => {
    if (!tDraft?.id) return;
    setTheatreSaving(true);
    axios
      .post(Constants.ADMIN_ADD_UPDATE_THEATRE, {
        id: tDraft.id,
        name: tDraft.name,
        description: tDraft.description,
        minGuests: tDraft.minGuests === "" ? null : Number(tDraft.minGuests),
        maxGuests: tDraft.maxGuests === "" ? null : Number(tDraft.maxGuests),
        basePrice: tDraft.basePrice === "" ? null : Number(tDraft.basePrice),
        extraGuestPrice: tDraft.extraGuestPrice === "" ? null : Number(tDraft.extraGuestPrice),
        defaultCapacity:
          tDraft.defaultCapacity === "" ? 0 : Number(tDraft.defaultCapacity),
        imageLocation: tDraft.imageLocation || null,
        isActive: tDraft.isActive === "Y" ? "Y" : "N",
        availableSlots:
          tDraft.availableSlots === "" ? 0 : Number(tDraft.availableSlots),
      })
      .then(() => {
        showBanner("success", "Theatre saved.");
        loadMeta();
      })
      .catch(() => showBanner("danger", "Could not save theatre."))
      .finally(() => setTheatreSaving(false));
  };

  const saveSlotOffer = (dto) => {
    setOfferBusy(true);
    axios
      .post(Constants.ADMIN_ADD_UPDATE_SLOT_TIME_OFFER, dto)
      .then(() => {
        showBanner("success", "Slot offer saved.");
        setEditingOfferId(null);
        loadMeta();
      })
      .catch(() => showBanner("danger", "Could not save slot offer."))
      .finally(() => setOfferBusy(false));
  };

  const deleteSlotOffer = (id) => {
    if (!id || !window.confirm("Delete this slot-time offer?")) return;
    setOfferBusy(true);
    axios
      .post(Constants.ADMIN_DELETE_SLOT_TIME_OFFER, { id })
      .then(() => {
        showBanner("success", "Offer deleted.");
        loadMeta();
      })
      .catch(() => showBanner("danger", "Could not delete offer."))
      .finally(() => setOfferBusy(false));
  };

  const submitNewOffer = () => {
    if (!newOffer.theatreId || !newOffer.slotId) {
      showBanner("warning", "Pick a theatre and slot for the offer.");
      return;
    }
    const v = Number(newOffer.offerBasePrice);
    if (!Number.isFinite(v) || v < 0) {
      showBanner("warning", "Enter a valid amount or percentage.");
      return;
    }
    if (String(newOffer.offerPriceKind).toUpperCase() === "PCT" && v > 100) {
      showBanner("warning", "Percentage off cannot exceed 100.");
      return;
    }
    saveSlotOffer({
      id: editingOfferId != null ? Number(editingOfferId) : null,
      theatreId: Number(newOffer.theatreId),
      slotId: Number(newOffer.slotId),
      validFrom: newOffer.validFrom,
      validTo: newOffer.validTo,
      offerBasePrice: Math.round(v),
      offerPriceKind: String(newOffer.offerPriceKind || "RS").toUpperCase(),
      headline: newOffer.headline || null,
      isActive: newOffer.isActive,
    });
  };

  const startEditOffer = (o) => {
    if (!o?.id) return;
    setEditingOfferId(o.id);
    setNewOffer((prev) => ({
      ...prev,
      theatreId: String(o.theatreId ?? ""),
      slotId: String(o.slotId ?? ""),
      validFrom: (o.validFrom || "").slice(0, 10),
      validTo: (o.validTo || "").slice(0, 10),
      offerBasePrice:
        o.offerBasePrice != null && o.offerBasePrice !== "" ? String(o.offerBasePrice) : "",
      offerPriceKind: String(o.offerPriceKind || "RS").toUpperCase() === "PCT" ? "PCT" : "RS",
      headline: o.headline || "",
      isActive: o.isActive === "N" ? "N" : "Y",
    }));
  };

  const cancelEditOffer = () => {
    setEditingOfferId(null);
    setNewOffer((p) => ({
      ...p,
      slotId: filterTheatreId ? "" : p.slotId,
      offerBasePrice: "",
      headline: "",
      offerPriceKind: "RS",
    }));
  };

  if (loadingMeta) {
    return (
      <div className="py-4 text-center text-secondary">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading theatre data…
      </div>
    );
  }

  return (
    <div className="admin-theatre-details">
      <p className="text-secondary small mb-3">
        Edit theatre and slot rows below (saved to <code className="text-info">theatre_meta</code> /{" "}
        <code className="text-info">slots_meta</code>).{" "}
        <strong className="text-light">Time-slot offers</strong> apply a different base package price for a date range
        on the Book now flow; customers cannot stack promo codes when an offer applies.
      </p>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <Form.Label>Filter by theatre</Form.Label>
          <Form.Select
            value={filterTheatreId}
            onChange={(e) => setFilterTheatreId(e.target.value)}
          >
            <option value="">All theatres</option>
            {theatres.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <h6 className="text-white mb-2">Theatres</h6>
      {!filterTheatreId ? (
        <Alert variant="secondary" className="small mb-3">
          Select a theatre to edit details. Below is a read-only list of all venues.
        </Alert>
      ) : null}

      {filterTheatreId && tDraft ? (
        <div className="admin-console-card mb-4 p-3">
          <div className="admin-console-card-title mb-2">Edit theatre</div>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Label className="small text-secondary">Name</Form.Label>
              <Form.Control
                size="sm"
                value={tDraft.name}
                onChange={(e) => setTDraft((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <Form.Label className="small text-secondary">Active</Form.Label>
              <Form.Select
                size="sm"
                value={tDraft.isActive === "Y" ? "Y" : "N"}
                onChange={(e) => setTDraft((p) => ({ ...p, isActive: e.target.value }))}
              >
                <option value="Y">Y</option>
                <option value="N">N</option>
              </Form.Select>
            </div>
            <div className="col-12">
              <Form.Label className="small text-secondary">Description / location notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={tDraft.description}
                onChange={(e) => setTDraft((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <Form.Label className="small text-secondary">Min guests</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={tDraft.minGuests}
                onChange={(e) => setTDraft((p) => ({ ...p, minGuests: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <Form.Label className="small text-secondary">Max guests</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={tDraft.maxGuests}
                onChange={(e) => setTDraft((p) => ({ ...p, maxGuests: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <Form.Label className="small text-secondary">Default capacity</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={tDraft.defaultCapacity}
                onChange={(e) => setTDraft((p) => ({ ...p, defaultCapacity: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <Form.Label className="small text-secondary">Avail. slots (display)</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={tDraft.availableSlots}
                onChange={(e) => setTDraft((p) => ({ ...p, availableSlots: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <Form.Label className="small text-secondary">Theatre base ₹</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={tDraft.basePrice}
                onChange={(e) => setTDraft((p) => ({ ...p, basePrice: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <Form.Label className="small text-secondary">Extra guest ₹</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={tDraft.extraGuestPrice}
                onChange={(e) => setTDraft((p) => ({ ...p, extraGuestPrice: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <Form.Label className="small text-secondary">Image path</Form.Label>
              <Form.Control
                size="sm"
                value={tDraft.imageLocation}
                onChange={(e) => setTDraft((p) => ({ ...p, imageLocation: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <Button type="button" variant="success" disabled={theatreSaving} onClick={saveTheatre}>
                {theatreSaving ? "Saving…" : "Save theatre"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="table-responsive mb-4">
        <Table striped bordered hover variant="dark" size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Location / notes</th>
              <th>Min</th>
              <th>Max</th>
              <th>Slots</th>
              <th>Offers / pricing</th>
            </tr>
          </thead>
          <tbody>
            {filteredTheatres.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-secondary">
                  No theatres returned.
                </td>
              </tr>
            ) : (
              filteredTheatres.map((t) => {
                const slotCount = Array.isArray(t.timeslots) ? t.timeslots.length : 0;
                return (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="small">{t.description || "—"}</td>
                    <td>{t.minGuests ?? "—"}</td>
                    <td>{t.maxGuests ?? "—"}</td>
                    <td>{slotCount}</td>
                    <td className="small">
                      Base {rupees(t.basePrice)}
                      {t.extraGuestPrice != null ? (
                        <>
                          <br />
                          +{rupees(t.extraGuestPrice)} / extra guest
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <h6 className="text-white mb-2">Time slots</h6>
      {!filterTheatreId ? (
        <Alert variant="secondary" className="small mb-2">
          Select a theatre to edit slot rows.
        </Alert>
      ) : null}
      <div className="table-responsive mb-4">
        <Table striped bordered hover variant="dark" size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Theatre</th>
              <th>Slot name</th>
              <th>Time label</th>
              <th>Start – End</th>
              <th>Min–Max</th>
              <th>Base ₹</th>
              <th>Slot promo</th>
              <th>Promo value</th>
              <th>Active</th>
              <th>Extra / cap / save</th>
            </tr>
          </thead>
          <tbody>
            {!filterTheatreId ? (
              <tr>
                <td colSpan={10} className="text-secondary">
                  Pick a theatre to load editable slot rows.
                </td>
              </tr>
            ) : slotsForFilter.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-secondary">
                  No slots for this theatre.
                </td>
              </tr>
            ) : (
              slotsForFilter.map((s) => {
                const th = theatres.find((x) => String(x.id) === String(s.theatreId));
                return (
                  <SlotRowEditor
                    key={s.id}
                    slot={s}
                    theatreName={th?.name ?? s.theatreId}
                    onSaved={loadMeta}
                    showBanner={showBanner}
                  />
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <h6 className="text-white mb-2">Time-slot offers (date range)</h6>
      <p className="text-secondary small">
        Shown on Book now in <span className="text-danger">red</span> for the matching theatre, slot, and event date.
        Promo codes are blocked for those bookings.
      </p>
      <div className="admin-console-card mb-4 p-3">
        {editingOfferId ? (
          <Alert variant="info" className="small py-2 mb-2">
            Editing offer #{editingOfferId}.{" "}
            <Button type="button" variant="link" size="sm" className="p-0 align-baseline" onClick={cancelEditOffer}>
              Cancel
            </Button>
          </Alert>
        ) : null}
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <Form.Label className="small text-secondary">Theatre</Form.Label>
            <Form.Select
              size="sm"
              value={newOffer.theatreId}
              onChange={(e) => setNewOffer((p) => ({ ...p, theatreId: e.target.value, slotId: "" }))}
              disabled={Boolean(editingOfferId)}
            >
              <option value="">—</option>
              {theatres.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Label className="small text-secondary">Slot</Form.Label>
            <Form.Select
              size="sm"
              value={newOffer.slotId}
              onChange={(e) => setNewOffer((p) => ({ ...p, slotId: e.target.value }))}
              disabled={!newOffer.theatreId || Boolean(editingOfferId)}
            >
              <option value="">—</option>
              {allSlotsFlat
                .filter((sl) => String(sl.theatreId) === String(newOffer.theatreId))
                .map((sl) => (
                  <option key={sl.id} value={String(sl.id)}>
                    {sl.name || sl.slotTime || sl.id}
                  </option>
                ))}
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Form.Label className="small text-secondary">From</Form.Label>
            <AdminDateInput
              size="sm"
              value={newOffer.validFrom}
              onChange={(e) => setNewOffer((p) => ({ ...p, validFrom: e.target.value }))}
            />
          </div>
          <div className="col-md-2">
            <Form.Label className="small text-secondary">To</Form.Label>
            <AdminDateInput
              size="sm"
              value={newOffer.validTo}
              onChange={(e) => setNewOffer((p) => ({ ...p, validTo: e.target.value }))}
            />
          </div>
          <div className="col-md-2">
            <Form.Label className="small text-secondary">₹ or % off</Form.Label>
            <Form.Select
              size="sm"
              value={newOffer.offerPriceKind}
              onChange={(e) => setNewOffer((p) => ({ ...p, offerPriceKind: e.target.value }))}
            >
              <option value="RS">₹ off listed slot base</option>
              <option value="PCT">% (off slot base)</option>
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Form.Label className="small text-secondary">Amount or percentage</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              max={String(newOffer.offerPriceKind).toUpperCase() === "PCT" ? 100 : undefined}
              placeholder={
                String(newOffer.offerPriceKind).toUpperCase() === "PCT" ? "0–100" : "Rupees"
              }
              value={newOffer.offerBasePrice}
              onChange={(e) => setNewOffer((p) => ({ ...p, offerBasePrice: e.target.value }))}
            />
          </div>
          <div className="col-12">
            <Form.Label className="small text-secondary">
              Customer message (extra red line on Book now — optional)
            </Form.Label>
            <Form.Control
              size="sm"
              value={newOffer.headline}
              onChange={(e) => setNewOffer((p) => ({ ...p, headline: e.target.value }))}
              placeholder="e.g. Mon–Wed special for this slot"
            />
          </div>
          <div className="col-md-2">
            <Form.Label className="small text-secondary">Active</Form.Label>
            <Form.Select
              size="sm"
              value={newOffer.isActive}
              onChange={(e) => setNewOffer((p) => ({ ...p, isActive: e.target.value }))}
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </Form.Select>
          </div>
          <div className="col-md-6 d-flex flex-wrap gap-2 align-items-end">
            <Button type="button" variant="primary" disabled={offerBusy} onClick={submitNewOffer}>
              {offerBusy ? "Saving…" : editingOfferId != null ? "Update offer" : "Add offer"}
            </Button>
            {editingOfferId ? (
              <Button type="button" variant="outline-secondary" disabled={offerBusy} onClick={cancelEditOffer}>
                Cancel edit
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="table-responsive mb-4">
        <Table striped bordered hover variant="dark" size="sm" className="align-middle">
          <thead>
            <tr>
              <th>ID</th>
              <th>Theatre</th>
              <th>Slot</th>
              <th>Dates</th>
              <th>Kind</th>
              <th>Value</th>
              <th>Message</th>
              <th>Act</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSlotOffers.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-secondary">
                  {filterTheatreId ? "No slot offers for this theatre." : "No slot offers (or filter by theatre)."}
                </td>
              </tr>
            ) : (
              filteredSlotOffers.map((o) => {
                const th = theatres.find((x) => String(x.id) === String(o.theatreId));
                const sl = allSlotsFlat.find((x) => String(x.id) === String(o.slotId));
                return (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td className="small">{th?.name ?? o.theatreId}</td>
                    <td className="small">{sl?.name || sl?.slotTime || o.slotId}</td>
                    <td className="text-nowrap small">
                      {(o.validFrom || "").slice(0, 10)} → {(o.validTo || "").slice(0, 10)}
                    </td>
                    <td className="small">
                      {String(o.offerPriceKind || "RS").toUpperCase() === "PCT" ? "% off" : "₹ off"}
                    </td>
                    <td className="small">{displaySlotOfferValue(o)}</td>
                    <td className="small text-danger">{o.headline || "—"}</td>
                    <td>{o.isActive === "Y" ? "Y" : "N"}</td>
                    <td className="text-nowrap">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-light"
                        className="me-1"
                        disabled={offerBusy}
                        onClick={() => startEditOffer(o)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-danger"
                        disabled={offerBusy}
                        onClick={() => deleteSlotOffer(o.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <h6 className="text-white mb-2">Custom message (per theatre)</h6>
      {!filterTheatreId ? (
        <Alert variant="secondary" className="small">
          Select a single theatre above to load and edit its custom message file.
        </Alert>
      ) : noteLoading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small text-secondary">
              Message for <strong className="text-light">{selectedTheatre?.name}</strong> (storage key{" "}
              <code>{noteKey}</code>)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Shown to staff or on future site features — stored as UTF-8 text beside gallery images."
            />
          </Form.Group>
          <Button type="button" variant="primary" disabled={noteSaving} onClick={saveNote}>
            {noteSaving ? "Saving…" : "Save message to shared folder"}
          </Button>
        </>
      )}

      <h6 className="text-white mb-2 mt-4">Book now — experience lines (icons + text)</h6>
      {!filterTheatreId ? (
        <Alert variant="secondary" className="small">
          Select a theatre to edit bullets shown on venue cards (saved as JSON next to gallery files).
        </Alert>
      ) : hlLoading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <>
          <p className="text-secondary small">
            Bootstrap Icons class per line (e.g. <code className="text-info">bi-currency-rupee</code>). Use{" "}
            <strong>Pick icon</strong> for common venue icons, or type any class name.
          </p>
          <div className="d-flex flex-column gap-2 mb-2">
            {hlLines.map((line, idx) => (
              <div key={idx} className="row g-2 align-items-center">
                <div className="col-md-3">
                  <InputGroup size="sm">
                    <Form.Control
                      placeholder="bi-heart-fill"
                      value={line.icon}
                      onChange={(e) => {
                        const next = [...hlLines];
                        next[idx] = { ...next[idx], icon: e.target.value };
                        setHlLines(next);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      title="Choose from list"
                      aria-label="Open icon picker"
                      onClick={() => setIconPickerLineIndex(idx)}
                    >
                      <i className="bi bi-grid-3x3-gap" aria-hidden />
                    </Button>
                  </InputGroup>
                  {line.icon ? (
                    <div className="mt-1 small text-secondary">
                      Preview: <i className={line.icon} aria-hidden />
                    </div>
                  ) : null}
                </div>
                <div className="col-md-7">
                  <Form.Control
                    size="sm"
                    placeholder="Visible text on card"
                    value={line.text}
                    onChange={(e) => {
                      const next = [...hlLines];
                      next[idx] = { ...next[idx], text: e.target.value };
                      setHlLines(next);
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setHlLines((rows) => rows.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-2 mb-2">
            <Button
              type="button"
              size="sm"
              variant="outline-light"
              onClick={() => setHlLines((rows) => [...rows, { icon: "", text: "" }])}
            >
              Add line
            </Button>
            <Button type="button" variant="success" size="sm" disabled={hlSaving} onClick={saveHighlights}>
              {hlSaving ? "Saving…" : "Save experience lines to shared folder"}
            </Button>
          </div>
        </>
      )}
      <Modal
        show={iconPickerLineIndex !== null}
        onHide={() => setIconPickerLineIndex(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose an icon</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">
          <p className="text-secondary small">
            Tap an icon to use its Bootstrap Icons class on this line. You can still edit the field manually.
          </p>
          <div
            className="d-flex flex-wrap gap-2"
            style={{ maxHeight: "min(60vh, 420px)", overflowY: "auto" }}
          >
            {THEATRE_EXPERIENCE_ICONS.map((cls) => (
              <Button
                key={cls}
                type="button"
                variant="outline-light"
                className="d-inline-flex flex-column align-items-center gap-1 px-2 py-2"
                style={{ minWidth: "5.5rem" }}
                onClick={() => {
                  if (iconPickerLineIndex == null) return;
                  setHlLines((rows) => {
                    const next = [...rows];
                    const row = next[iconPickerLineIndex];
                    if (row) next[iconPickerLineIndex] = { ...row, icon: cls };
                    return next;
                  });
                  setIconPickerLineIndex(null);
                }}
              >
                <i className={`${cls} fs-4`} aria-hidden />
                <span className="small text-truncate" style={{ maxWidth: "5rem" }} title={cls}>
                  {cls.replace(/^bi-/, "")}
                </span>
              </Button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
